<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import CampaignsAPI from 'dashboard/api/campaigns';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const isLoading = ref(true);
const variants = ref([]);
const aiAnalysis = ref('');
const isFetchingAnalysis = ref(false);

const metricLabel = key => {
  const labels = {
    total: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.TOTAL'),
    delivered: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.DELIVERED'),
    read: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.READ'),
    failed: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.FAILED'),
  };
  return labels[key] || key;
};

const readRate = metrics => {
  if (!metrics?.total) return 0;
  return Math.round(((metrics.read || 0) / metrics.total) * 100);
};

const deliveryRate = metrics => {
  if (!metrics?.total) return 0;
  const delivered = (metrics.delivered || 0) + (metrics.read || 0);
  return Math.round((delivered / metrics.total) * 100);
};

const fetchComparison = async () => {
  isLoading.value = true;
  try {
    const response = await CampaignsAPI.abComparison(props.campaign.id);
    variants.value = response.data.variants;
  } catch {
    variants.value = [];
  } finally {
    isLoading.value = false;
  }
};

const fetchAIAnalysis = async () => {
  isFetchingAnalysis.value = true;
  try {
    const response = await CampaignsAPI.abWinner(props.campaign.id);
    aiAnalysis.value = response.data.analysis;
  } catch {
    aiAnalysis.value = '';
  } finally {
    isFetchingAnalysis.value = false;
  }
};

const winnerVariant = () => {
  if (variants.value.length < 2) return null;
  const [a, b] = variants.value;
  const rateA = readRate(a.campaign_metrics);
  const rateB = readRate(b.campaign_metrics);
  if (rateA === rateB) return null;
  return rateA > rateB ? a.variant_label : b.variant_label;
};

onMounted(fetchComparison);
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-2">
    <div
      class="relative flex flex-col w-full max-w-3xl max-h-[85vh] bg-n-background rounded-xl border border-n-weak shadow-lg"
    >
      <header
        class="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-n-weak"
      >
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.AB_TEST.COMPARISON.TITLE') }}
          </h3>
          <span class="text-sm text-n-slate-11">
            {{ campaign.title }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <Button
            v-if="!aiAnalysis && !isFetchingAnalysis"
            :label="t('CAMPAIGN.WHATSAPP.AB_TEST.COMPARISON.AI_ANALYSIS')"
            variant="faded"
            size="sm"
            color="slate"
            icon="i-lucide-sparkles"
            @click="fetchAIAnalysis"
          />
          <Button
            variant="ghost"
            size="sm"
            color="slate"
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>
      </header>

      <div
        v-if="isLoading"
        class="flex items-center justify-center flex-1 py-16"
      >
        <Spinner />
      </div>
      <div v-else class="flex flex-col flex-1 gap-6 p-6 overflow-y-auto">
        <div
          v-if="isFetchingAnalysis"
          class="flex items-center gap-2 p-4 rounded-lg border border-n-weak bg-n-alpha-1"
        >
          <Spinner class="w-4 h-4" />
          <span class="text-sm text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.AB_TEST.COMPARISON.ANALYZING') }}
          </span>
        </div>
        <div
          v-else-if="aiAnalysis"
          class="p-4 rounded-lg border border-n-weak bg-n-alpha-1"
        >
          <div class="flex items-center gap-2 mb-2">
            <span class="text-sm i-lucide-sparkles text-n-violet-11" />
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.AB_TEST.COMPARISON.AI_INSIGHTS') }}
            </h4>
          </div>
          <div
            v-dompurify-html="aiAnalysis"
            class="text-sm text-n-slate-11 prose prose-sm max-w-none"
          />
        </div>

        <!-- Variant comparison cards -->
        <div class="grid grid-cols-2 gap-4">
          <div
            v-for="variant in variants"
            :key="variant.id"
            class="flex flex-col gap-3 p-4 rounded-lg border"
            :class="
              winnerVariant() === variant.variant_label
                ? 'border-n-teal-6 bg-n-teal-2'
                : 'border-n-weak bg-n-alpha-1'
            "
          >
            <div class="flex items-center justify-between">
              <span
                class="text-sm font-semibold px-2 py-0.5 rounded"
                :class="
                  variant.variant_label === 'A'
                    ? 'bg-n-blue-3 text-n-blue-11'
                    : 'bg-n-violet-3 text-n-violet-11'
                "
              >
                {{ t('CAMPAIGN.WHATSAPP.AB_TEST.COMPARISON.VARIANT') }}
                {{ variant.variant_label }}
              </span>
              <span
                v-if="winnerVariant() === variant.variant_label"
                class="flex items-center gap-1 text-xs font-medium text-n-teal-11"
              >
                <span class="i-lucide-trophy text-xs" />
                {{ t('CAMPAIGN.WHATSAPP.AB_TEST.COMPARISON.WINNER') }}
              </span>
            </div>
            <p class="text-sm text-n-slate-11 line-clamp-2">
              {{ variant.title }}
            </p>
            <div class="flex flex-col gap-2">
              <div
                v-for="key in ['total', 'delivered', 'read', 'failed']"
                :key="key"
                class="flex items-center justify-between text-sm"
              >
                <span class="text-n-slate-11">{{ metricLabel(key) }}</span>
                <span class="font-medium text-n-slate-12">
                  {{ variant.campaign_metrics?.[key] || 0 }}
                </span>
              </div>
              <div class="pt-2 border-t border-n-weak">
                <div
                  class="flex items-center justify-between text-sm font-medium"
                >
                  <span class="text-n-slate-11">
                    {{
                      t('CAMPAIGN.WHATSAPP.AB_TEST.COMPARISON.DELIVERY_RATE')
                    }}
                  </span>
                  <span class="text-n-slate-12">
                    {{ `${deliveryRate(variant.campaign_metrics)}%` }}
                  </span>
                </div>
                <div
                  class="flex items-center justify-between text-sm font-medium mt-1"
                >
                  <span class="text-n-slate-11">
                    {{ t('CAMPAIGN.WHATSAPP.AB_TEST.COMPARISON.READ_RATE') }}
                  </span>
                  <span class="text-n-slate-12">
                    {{ `${readRate(variant.campaign_metrics)}%` }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
